import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import {
  SearchBarAtom,
  ChipAtom,
  TemplateCardMolecule,
  getApplicableTemplates,
  useApiClientsOrNull,
} from 'design-system/my-lib';
import type { Template, TemplateApproach } from 'design-system/my-lib';
import './OrgTemplatePickerMolecule.scss';

export type { Template };

export interface OrgTemplatePickerMoleculeProps {
  value: Template | null;
  onChange: (value: Template | null) => void;
}

// Map the endpoint's count shape to the design-system's TemplateApproach count
// fields. The semantics differ by domain (approach vs org-template), but the UI
// treatment — count chips in the card body — is identical and reusing
// TemplateCardMolecule avoids duplicating the card layout.
function toTemplateApproach(t: Template): TemplateApproach {
  return {
    id: t.systemName,
    system_name: t.systemName,
    name: t.displayName,
    description: t.description ?? '',
    use_case_tags: [],
    applicability: null,
    templateFor: t.templateFor,
    childCount: t.counts.subApproaches,
    factorCount: t.counts.providedTypes,
    metaspaceCount: t.counts.providedContexts,
    official: t.official,
    isNew: t.isNew ?? false,
    channel: t.channel
      ? {
          id: t.channel.systemName,
          system_name: t.channel.systemName,
          name: t.channel.displayName,
          subscribed: t.channel.subscribed,
        }
      : undefined,
  };
}

const OrgTemplatePickerMolecule: React.FC<OrgTemplatePickerMoleculeProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeChannel, setActiveChannel] = useState<string | null>(null);

  // Single round-trip replacing the previous N+1 listChannels → getChannelItems
  // → per-item getApproach pattern. Foundation is always included by the endpoint
  // (channel=null, recommended=true); subscribed-channel templates follow.
  useEffect(() => {
    if (!apiClients?.schemaClient) return;
    let cancelled = false;
    setLoading(true);
    getApplicableTemplates(apiClients.schemaClient, { templateFor: 'center_organization' })
      .then((res) => {
        if (cancelled) return;
        setTemplates(res.templates ?? []);
        // Default-select Foundation (first recommended row).
        const foundation = res.templates.find((tmpl) => tmpl.recommended);
        if (foundation) onChange(foundation);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // onChange is intentionally excluded — we only want this to fire once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClients]);

  // Counts derived from the full, unfiltered set so the numbers stay stable as
  // the user filters — matches the "All channels 14 · Center Platform 4" behaviour
  // in the mockup.
  const channelNames = useMemo(() => {
    const seen = new Map<string, { displayName: string; count: number }>();
    for (const tmpl of templates) {
      if (!tmpl.channel) continue;
      const existing = seen.get(tmpl.channel.systemName);
      if (existing) {
        existing.count += 1;
      } else {
        seen.set(tmpl.channel.systemName, {
          displayName: tmpl.channel.displayName,
          count: 1,
        });
      }
    }
    return Array.from(seen, ([systemName, v]) => ({
      systemName,
      displayName: v.displayName,
      count: v.count,
    }));
  }, [templates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((tmpl) => {
      if (activeChannel && tmpl.channel?.systemName !== activeChannel) return false;
      if (q && !tmpl.displayName.toLowerCase().includes(q) && !(tmpl.description ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [templates, search, activeChannel]);

  const foundation = filtered.find((tmpl) => tmpl.recommended && !tmpl.channel);
  const channelGroups = useMemo(() => {
    const groups: Record<string, Template[]> = {};
    for (const tmpl of filtered) {
      if (!tmpl.channel) continue;
      const key = tmpl.channel.systemName;
      if (!groups[key]) groups[key] = [];
      groups[key].push(tmpl);
    }
    return groups;
  }, [filtered]);

  if (loading) {
    return (
      <div className="org-template-picker org-template-picker--loading" data-testid="org-template-picker">
        <i className="pi pi-spin pi-spinner" aria-hidden="true" />
        <span>{t('admin.organizations.addDialog.approach.loading')}</span>
      </div>
    );
  }

  if (!loading && templates.length === 0) {
    return (
      <div className="org-template-picker org-template-picker--empty" data-testid="org-template-picker">
        <span data-testid="org-template-picker-empty">
          {t('admin.organizations.addDialog.approach.empty')}
        </span>
      </div>
    );
  }

  return (
    <div className="org-template-picker" data-testid="org-template-picker">
      {/* Search + count */}
      <div className="org-template-picker__toolbar">
        <SearchBarAtom
          value={search}
          onChange={setSearch}
          placeholder={t('admin.organizations.addDialog.approach.searchPlaceholder')}
          data-testid="org-template-search"
        />
        <span className="org-template-picker__count" data-testid="org-template-count">
          {activeChannel || search.trim()
            ? t('admin.organizations.addDialog.approach.templateCount', {
                count: filtered.length,
              })
            : t('admin.organizations.addDialog.approach.templateAndChannelSummary', {
                count: templates.length,
                channelCount: channelNames.length,
              })}
        </span>
      </div>

      {/* Channel filter chips — "All channels" first (default active), then per-channel */}
      <div className="org-template-picker__chips" data-testid="channel-chips">
        <ChipAtom
          key="__all__"
          label={t('admin.organizations.addDialog.approach.allChannels')}
          template={
            <>
              <span className="p-chip-text">
                {t('admin.organizations.addDialog.approach.allChannels')}
              </span>
              <span className="org-template-picker__chip-count">{templates.length}</span>
            </>
          }
          className={`org-template-picker__chip org-template-picker__chip--all${activeChannel === null ? ' org-template-picker__chip--active' : ''}`}
          onClick={() => setActiveChannel(null)}
          data-testid="channel-chip-all"
        />
        {channelNames.map((ch) => (
          <ChipAtom
            key={ch.systemName}
            label={ch.displayName}
            template={
              <>
                <span className="p-chip-text">{ch.displayName}</span>
                <span className="org-template-picker__chip-count">{ch.count}</span>
              </>
            }
            className={`org-template-picker__chip${activeChannel === ch.systemName ? ' org-template-picker__chip--active' : ''}`}
            onClick={() => setActiveChannel((prev) => (prev === ch.systemName ? null : ch.systemName))}
            data-testid={`channel-chip-${ch.systemName}`}
          />
        ))}
      </div>

      {/* Foundation card — always first when visible */}
      {foundation && (
        <div className="org-template-picker__section" data-testid="foundation-section">
          <TemplateCardMolecule
            template={toTemplateApproach(foundation)}
            onClick={() => onChange(value?.systemName === foundation.systemName ? null : foundation)}
            selected={value?.systemName === foundation.systemName}
            data-testid={`org-template-card-${foundation.systemName}`}
          />
        </div>
      )}

      {/* Channel groups */}
      {Object.entries(channelGroups).map(([channelSystemName, groupTemplates]) => {
        const channelMeta = groupTemplates[0]?.channel;
        return (
          <div
            key={channelSystemName}
            className="org-template-picker__channel-group"
            data-testid={`channel-group-${channelSystemName}`}
          >
            <div className="org-template-picker__channel-header">
              <span className="org-template-picker__channel-name">
                {channelMeta?.displayName ?? channelSystemName}
              </span>
              <span className="org-template-picker__channel-meta">
                {channelMeta?.subscribed && (
                  <ChipAtom
                    label={t('admin.organizations.addDialog.approach.subscribed')}
                    icon="pi pi-check"
                    className="org-template-picker__subscribed-chip"
                  />
                )}
                {channelMeta?.marketplace && (
                  <ChipAtom
                    label={t('admin.organizations.addDialog.approach.marketplace')}
                    icon="pi pi-shopping-cart"
                    className="org-template-picker__marketplace-chip"
                  />
                )}
              </span>
            </div>
            <div className="org-template-picker__cards">
              {groupTemplates.map((tmpl) => (
                <TemplateCardMolecule
                  key={tmpl.systemName}
                  template={toTemplateApproach(tmpl)}
                  onClick={() => onChange(value?.systemName === tmpl.systemName ? null : tmpl)}
                  selected={value?.systemName === tmpl.systemName}
                  data-testid={`org-template-card-${tmpl.systemName}`}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* "Draft with agent" — display-only coming-soon card */}
      <div
        className="org-template-picker__agent-card"
        aria-disabled="true"
        data-testid="agent-card"
      >
        <i className="pi pi-sparkles org-template-picker__agent-icon" aria-hidden="true" />
        <div className="org-template-picker__agent-text">
          <span className="org-template-picker__agent-title">
            {t('admin.organizations.addDialog.approach.agentCard.title')}
          </span>
          <span className="org-template-picker__agent-subtitle">
            {t('admin.organizations.addDialog.approach.agentCard.comingSoon')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OrgTemplatePickerMolecule;
